import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useConfig } from '@context/ConfigContext';
import Button from '@components/shared/Button/Button';
import styles from './Hero.module.css';

const Hero: React.FC = () => {
  const { config } = useConfig();
  const [currentSlide, setCurrentSlide] = useState(0);

  if (!config || !config.components.hero.enabled) return null;

  const { hero } = config.components;
  const { slideshow } = hero;

  useEffect(() => {
    if (!slideshow.enabled || !slideshow.autoplay || slideshow.images.length <= 1) {
      return;
    }

    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slideshow.images.length);
    }, slideshow.interval);

    return () => clearInterval(interval);
  }, [slideshow]);

  const backgroundImage = slideshow.enabled && slideshow.images.length > 0
    ? slideshow.images[currentSlide]
    : hero.backgroundImage;

  const handleScrollDown = () => {
    const aboutSection = document.querySelector('#about');
    if (aboutSection) {
      const headerOffset = 80;
      const elementPosition = aboutSection.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth',
      });
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.3,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: 'easeOut',
      },
    },
  };

  return (
    <section id="home" className={styles.hero}>
      {/* Background Image with Transition */}
      <div
        className={styles.background}
        style={{
          backgroundImage: `url('${backgroundImage}')`,
        }}
      >
        <div className={styles.overlay}></div>
      </div>

      {/* Content */}
      <div className={styles.container}>
        <motion.div
          className={styles.content}
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <motion.h1 className={styles.title} variants={itemVariants}>
            {hero.title}
          </motion.h1>

          <motion.p className={styles.subtitle} variants={itemVariants}>
            {hero.subtitle}
          </motion.p>

          <motion.div className={styles.ctaButtons} variants={itemVariants}>
            {hero.cta.map((button, index) => (
              <Button
                key={index}
                variant={button.style as 'primary' | 'secondary'}
                size="lg"
                onClick={() => {
                  if (button.link.startsWith('#')) {
                    const element = document.querySelector(button.link);
                    if (element) {
                      const headerOffset = 80;
                      const elementPosition = element.getBoundingClientRect().top;
                      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

                      window.scrollTo({
                        top: offsetPosition,
                        behavior: 'smooth',
                      });
                    }
                  } else {
                    window.location.href = button.link;
                  }
                }}
              >
                {button.text}
              </Button>
            ))}
          </motion.div>
        </motion.div>
      </div>

      {/* Slideshow Indicators */}
      {slideshow.enabled && slideshow.images.length > 1 && (
        <div className={styles.indicators}>
          {slideshow.images.map((_, index) => (
            <button
              key={index}
              className={`${styles.indicator} ${
                index === currentSlide ? styles.active : ''
              }`}
              onClick={() => setCurrentSlide(index)}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      )}

      {/* Scroll Down Indicator */}
      <motion.div
        className={styles.scrollIndicator}
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{
          duration: 0.8,
          delay: 1.5,
          repeat: Infinity,
          repeatType: 'reverse',
          repeatDelay: 0.5,
        }}
        onClick={handleScrollDown}
        style={{ cursor: 'pointer' }}
      >
        <div className={styles.scrollArrow}>
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </div>
      </motion.div>
    </section>
  );
};

export default Hero;